import { useEffect, useState } from 'react';

import { resendAvailableInSeconds } from '../services/email-verification.service';

/**
 * The "you can request a new code in Ns" countdown for the credit step's email OTP
 * (E-SF05). Pure local UI state shared between the page and the OTP field, so it
 * lives in a hook per the CLAUDE.md rule.
 *
 * ── IT RECOMPUTES FROM THE SERVER'S INSTANT ON EVERY TICK ──────────────────
 * The obvious implementation seeds a counter with 60 and decrements it once a
 * second. That is wrong in exactly the situation a customer hits: they switch tabs
 * (or the OS suspends the tab) while waiting, the browser throttles or drops timer
 * callbacks, and the counter — which has no idea time passed — re-enables the button
 * late, or keeps it disabled long after the server would accept a resend.
 *
 * So the interval is only a REPAINT trigger. The value always comes from
 * `resendAvailableInSeconds(resendAvailableAt, new Date())`, i.e. from the absolute
 * instant the server sent. Any number of dropped ticks is self-healing, and the
 * countdown is also correct immediately on mount/re-mount.
 *
 * The interval clears itself once the window has elapsed — a settled countdown does
 * not need a heartbeat.
 */
export function useResendCooldown(resendAvailableAt: string | null): number {
  const [seconds, setSeconds] = useState<number>(() =>
    resendAvailableInSeconds(resendAvailableAt, new Date()),
  );

  useEffect(() => {
    const remaining = (): number => resendAvailableInSeconds(resendAvailableAt, new Date());

    // Re-seed synchronously: a new challenge (or a re-mount) must not wait a second
    // to show the right number.
    setSeconds(remaining());
    if (remaining() === 0) return undefined;

    const id = window.setInterval(() => {
      const next = remaining();
      setSeconds(next);
      if (next === 0) window.clearInterval(id);
    }, 1_000);
    return () => window.clearInterval(id);
  }, [resendAvailableAt]);

  return seconds;
}
